<template>
  <div :id="`${id}_cont`" class="form-group pb-2" v-if="showContent">
    <template v-if="!editMode">
      <div class="d-flex justify-content-between">
        <label v-if="title" :for="id" class="title form-label">{{ title }}</label>
        <!-- El contador (con max) va junto al mensaje de validación, no en su lugar -->
        <div class="field-status">
          <template v-if="!disableValidation && (feedback === 'invalid' || feedback === 'valid')">
            <p v-if="feedback === 'invalid'" class="invalid-feedback">{{ errorMsg }}</p>
            <p v-else-if="feedback === 'valid'" class="valid-feedback">{{ validMsg }}</p>
          </template>
          <template v-else-if="isRequired">
            <p class="invalid-feedback required">Dato obligatorio</p>
          </template>
          <template v-else-if="!isRequired && !disableValidation">
            <p class="invalid-feedback optional">Dato opcional</p>
          </template>
          <p v-if="max" class="selection-counter" :class="{ full: isFull }">{{ selected.length }} de {{ max }}</p>
        </div>
      </div>
    </template>
    <template v-else-if="editMode">
      <div class="d-flex">
        <label v-if="title" :for="id" class="title">{{ title }}</label>
        <template v-if="isRequired && !errorMsgForm && editMode">
          <p class="invalid-feedback required ms-1">*</p>
        </template>
      </div>
    </template>
    <div class="position-relative">
      <v-select
        :label="labelKey"
        :filterable="false"
        :options="filteredItems"
        :placeholder="placeholder"
        v-model="selected"
        @search="onSearch"
        @open="onOpenSelect"
        :clearable="true"
        class="form-control v-select"
        :class="{ disabled: disableSelect }"
        :editMode="editMode"
        @change="handleChange"
        :selectable="isSelectable"
        multiple
      >
        <template #no-options>
          Ingrese {{ noOptionsPlaceholder }}
        </template>
        <template v-if="isFull" #list-header>
          <li class="list-limit" role="status">{{ maxReachedMessage }}</li>
        </template>
        <!-- Con searchByAlias se muestra el código interno junto a la patente -->
        <template v-if="searchByAlias" #option="option">
          <span class="option-plate">{{ option.plate }}</span>
          <span v-if="option.alias && option.alias !== option.plate" class="option-alias">{{ option.alias }}</span>
        </template>
      </v-select>
    </div>
    <!-- Carga masiva: pegar un listado de patentes y seleccionar las que existen -->
    <div v-if="allowMassivePaste && typeFilter === 'vehicle_plate'" class="massive">
      <button type="button" class="massive-toggle" :aria-expanded="massiveOpen" :aria-controls="`${id}_massive`" @click="massiveOpen = !massiveOpen">
        <i class="fa-solid fa-paste" aria-hidden="true"></i> Pegar listado de patentes
      </button>
      <div v-if="massiveOpen" :id="`${id}_massive`" class="massive-panel">
        <label :for="`${id}_massive_text`" class="visually-hidden">Listado de patentes</label>
        <textarea :id="`${id}_massive_text`" v-model="massiveText" rows="4" class="form-control"
          placeholder="Pega las patentes separadas por coma, espacio o salto de línea. Ej.: AAAA-11, BBBB22"></textarea>
        <div class="massive-actions">
          <button type="button" class="massive-cancel" @click="closeMassivePaste">Cancelar</button>
          <VsudButton color="primary" size="sm" :load="massiveLoading" @click="applyMassivePaste">Agregar patentes</VsudButton>
        </div>
      </div>
      <p v-if="massiveResult" class="massive-result" :class="{ 'has-missing': massiveResult.notFound.length > 0 || massiveResult.overLimit > 0 }" role="status">
        {{ massiveResult.message }}
        <template v-if="massiveResult.overLimit > 0">
          {{ massiveResult.overLimit === 1 ? 'Una no se agregó' : `${massiveResult.overLimit} no se agregaron` }} porque el máximo es {{ max }}.
        </template>
        <template v-if="massiveResult.notFound.length > 0">
          <br>No encontradas, deshabilitadas o fuera de tu alcance ({{ massiveResult.notFound.length }}):
          {{ massiveResult.notFound.slice(0, 10).join(', ') }}<template v-if="massiveResult.notFound.length > 10"> y {{ massiveResult.notFound.length - 10 }} más</template>.
        </template>
      </p>
    </div>
  </div>
  <!-- Loading -->
  <div :id="`${id}_cont`" v-else>
    <div class="form-group pb-2">
      <label v-if="title" class="title placeholder-glow col-3">
        <span class="placeholder col-12"></span>
      </label>
      <div class="placeholder-glow">
        <input class="form-control placeholder col-12" />
      </div>
    </div>
  </div>
</template>

<script>
import "vue-select/dist/vue-select.css";
import vSelect from "vue-select";
import VsudButton from "./VsudButton.vue";

// get_vehicles_filter (migps_back company/views.py) devuelve hasta 150 vehículos por consulta.
const VEHICLES_FILTER_LIMIT = 150;

export default {
  name: "VsudSelectMultipleSearch",
  inject: ["api_provider", "urls_server", "securedWebStorage", "useful_functions"],
  components: { vSelect, VsudButton },
  props: {
    title: { type: String, default: "" },
    name: { type: String, default: "" },
    id: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    value: { type: String, default: "" },
    items: { type: Array, default: () => [] },
    errorMsg: { type: String, default: "Valor incorrecto o nulo" },
    validMsg: { type: String, default: "Completado correctamente" },
    typeFilter: { type: String, default: "" },
    userFilter: { type: String, default: "" },
    // Acota la búsqueda de vehículos a una empresa puntual (intersecta con el scope).
    companyId: { type: [Number, String], default: null },
    // Precarga un listado inicial acotado al abrir (sin exigir texto de búsqueda).
    preload: { type: Boolean, default: false },
    // Solo vehículos con el equipamiento MIGPS Protect (módulo driver_access).
    protectOnly: { type: Boolean, default: false },
    isRequired: Boolean,
    disableSelect: { type: Boolean, default: false },
    disableValidation: { type: Boolean, default: false },
    allFilter: { type: Boolean, default: false },
    filterSide: { type: Boolean, default: false },
    // Con filterSide es un array (patentes o ids)
    modelValue: { type: [String, Array], default: " " },
    editMode: { type: Boolean, default: false },
    // Solo con typeFilter="vehicle_plate": permite pegar un listado de patentes.
    allowMassivePaste: { type: Boolean, default: false },
    // Tope de elementos seleccionados (también acota la carga masiva).
    max: { type: Number, default: null },
    // Solo con vehículos: busca por patente o código interno y muestra el código en las opciones.
    searchByAlias: { type: Boolean, default: false },
    selectedHighways: { type: Array, default: () => [] }
  },
  data() {
    return {
      showContent: false,
      minLoadTime: 1500,
      loadStart: null,
      feedback: "",
      searchQuery: "",
      filteredItems: [],
      selected: [],
      firstTime: true,
      find_value: 0,
      timeout: null,
      allHighwaysList: [],
      allPorchsList: [],
      massiveOpen: false,
      massiveText: "",
      massiveLoading: false,
      massiveResult: null,
      // filteredItems es la lista precargada (no una búsqueda ni solo lo elegido)
      preloadListShown: false
    };
  },
  computed: {
    isFull() {
      return !!this.max && this.selected.length >= this.max;
    },
    maxReachedMessage() {
      const isVehicle = ["vehicle", "vehicle_plate"].includes(this.typeFilter);
      return isVehicle
        ? `Llegaste al máximo de ${this.max} patentes. Quita una para agregar otra.`
        : `Llegaste al máximo de ${this.max}. Quita uno para agregar otro.`;
    },
    labelKey() {
      if (this.typeFilter === "user") return "full_name_and_email";
      if (this.typeFilter === "vehicle" || this.typeFilter === "vehicle_plate") return "plate";
      if (
        ["chore","zone","rule","type_zone","client","porch","highway","vehicle_group"].includes(this.typeFilter)
      ) return "name";
      if (this.typeFilter === "device") return this.userFilter ? "full_device_name" : "id";
      return "";
    },
    noOptionsPlaceholder() {
      const messages = {
        user: "nombre o correo de usuario",
        vehicle: "patente de vehiculo",
        vehicle_plate: "patente de vehiculo",
        chore: "nombre de flota",
        type_zone: "nombre de tipo de zona",
        client: "nombre de cliente",
        zone: "nombre de zona",
        rule: "nombre de regla",
        device: "ID de dispositivo",
        highway: "nombre de autopista",
        porch: "nombre de pórtico",
        vehicle_group: "nombre de grupo de vehículos",
        default: "un valor de búsqueda"
      };
      return messages[this.typeFilter] || messages.default;
    }
  },
  mounted() {
    this.loadStart = Date.now();
    window.addEventListener("load", this.onLoad);
    setTimeout(() => { this.showContent = true; }, this.minLoadTime);

    if (this.modelValue.length === 0) {
      this.feedback = "null";
    } else if (this.filterSide) {
      this.onSearchBySelected();
    }

    const deselectElements = document.querySelectorAll('.vs__deselect');
    deselectElements.forEach(element => element.addEventListener('click', this.handleDeselect));
  },
  beforeUnmount() {
    window.removeEventListener("load", this.onLoad);
  },
  watch: {
    modelValue(newVal) {
      if (this.filterSide && Array.isArray(newVal) && newVal.length == 0) {
        this.filteredItems = [];
        if (this.selected && this.selected.length > 0) this.selected = [];
      }
      if (newVal === null) {
        this.filteredItems = [];
        if (this.isRequired) this.feedback = "invalid";
        this.searchQuery = "";
      } else {
        this.feedback = "valid";
        if (this.searchQuery.trim().length == 0 && this.firstTime) this.onSearchBySelected();
      }
      this.$emit("feedback", this.feedback);
    },
    selected(newVal) {
      if (Array.isArray(newVal)) {
        this.handleChange();
      }
    },
    selectedHighways(newHighways) {
      if (this.typeFilter === 'porch' && newHighways) {
        this.selected = this.filterPorchsByHighway(newHighways);
        this.$emit('update:modelValue', this.selected);
      }
    }
  },
  methods: {
    onSearchBySelected(options = {}) {
      if (this.$props.typeFilter === "user") this.get_filters_options(this.urls_server.get_users_filter);
      if (["vehicle","vehicle_plate"].includes(this.$props.typeFilter)) this.get_filters_options(this.urls_server.get_vehicles_filter, "back", options);
      if (this.$props.typeFilter === "client") this.get_filters_options(this.urls_server.get_clients_filter);
      if (this.$props.typeFilter === "driver") this.get_filters_options(this.urls_server.get_drivers_filter);
      if (this.$props.typeFilter === "company") this.get_filters_options(this.urls_server.get_companies_filter);
      if (this.$props.typeFilter === "device") this.get_filters_options(this.urls_server.get_gps_devices);
      if (this.$props.typeFilter === "vehicle_group") this.get_filters_options(this.urls_server.get_vehicle_groups_filter);
      if (this.$props.typeFilter === "highway") {
        if (this.$props.modelValue.length > 0) this.getAllPorchs(this.urls_server.get_all_porchs);
        this.getAllHighways(this.urls_server.get_all_highways);
      }
      if (this.$props.typeFilter === "porch") this.getAllPorchs(this.urls_server.get_all_porchs);
      if (this.$props.typeFilter === "type_zone") this.get_filters_options(`/type_zones/filter_text`, "mux");
      if (this.$props.typeFilter === "zone") this.get_filters_options(`/zones/filter_text`, "mux");
      if (this.$props.typeFilter === "rule") this.get_filters_options(`/rules/filter_text`, "mux");
    },
    onOpenSelect() {
      // Preload al abrir sin texto: lista inicial acotada sin auto-seleccionar (firstTime=false).
      // Se pide si lo que se muestra no es esa lista (vacía, una búsqueda o solo lo ya elegido).
      if (this.$props.preload && !this.searchQuery.trim() && !(this.preloadListShown && this.filteredItems.length)) {
        this.firstTime = false;
        this.searchQuery = "";
        this.onSearchBySelected({ preloadList: true });
      }
    },
    onSearch(text_search) {
      clearTimeout(this.timeout);
      this.searchQuery = text_search;
      if (this.searchQuery.trim().length >= 3) {
        this.timeout = setTimeout(() => {
          if (this.$props.typeFilter === "user") this.get_filters_options(this.urls_server.get_users_filter);
          if (this.$props.typeFilter === "vehicle_group") this.get_filters_options(this.urls_server.get_vehicle_groups_filter);
          if (this.$props.typeFilter === "highway") this.filterSelectSearchByInput(this.searchQuery, this.allHighwaysList);
          if (this.$props.typeFilter === "porch") this.filterSelectSearchByInput(this.searchQuery, this.allPorchsList);
          if (this.$props.typeFilter === "zone") this.get_filters_options(`/zones/filter_text`, "mux");
          if (this.$props.typeFilter === "type_zone") this.get_filters_options(`/type_zones/filter_text`, "mux");
          if (this.$props.typeFilter === "rule") this.get_filters_options(`/rules/filter_text`, "mux");
          if (this.$props.typeFilter === "client") this.get_filters_options(this.urls_server.get_clients_filter);
          if (["vehicle","vehicle_plate"].includes(this.$props.typeFilter)) this.get_filters_options(this.urls_server.get_vehicles_filter);
          if (this.$props.typeFilter === "device" && this.searchQuery.trim().length >= 5) this.get_filters_options(this.urls_server.get_gps_devices);
        }, 1000);
      } else {
        this.filteredItems = [];
      }
    },
    async get_filters_options(url, type_post="back", { preloadList = false } = {}) {
      const user_id = this.securedWebStorage.getItem("id");
      const id_company = this.securedWebStorage.getItem("id_company");
      const system_role = this.securedWebStorage.getItem("role");
      this.find_value++;
      let find_value_current = this.find_value;
      let user_filter = this.$props.userFilter;
      let all = this.$props.allFilter;

      // La lista precargada va sin la selección: con ella el back devuelve solo lo ya elegido
      const selection = preloadList ? [] : this.$props.modelValue;
      let postData = {
        search: this.searchQuery,
        user_filter: user_filter,
        ids_selected: this.$props.typeFilter != "vehicle_plate" ? selection : [],
        plates_selected: this.$props.typeFilter === "vehicle_plate" ? selection : [],
        all: all,
        id_company: id_company,
        user_id: user_id,
        system_role: system_role,
      };
      // Scope opcional por empresa puntual (p. ej. empresa del conductor en activación).
      if (this.$props.companyId) {
        postData.scope_company_id = this.$props.companyId;
      }
      // Solicita un listado inicial acotado cuando no hay búsqueda.
      if (this.$props.preload) {
        postData.preload = true;
      }
      // Solo vehículos con el equipamiento MIGPS Protect.
      if (this.$props.protectOnly) {
        postData.require_protect_equipment = true;
      }
      if (this.$props.searchByAlias) {
        postData.search_field = "plate_or_alias";
      }
      try {
        if(type_post == "back") {
          const response = await this.api_provider.post(url, postData);
          if (find_value_current < this.find_value) return;
          if (response.data && response.data.options_list) {
            this.filteredItems = response.data.options_list;
            this.preloadListShown = preloadList;
            if (this.firstTime && this.searchQuery.trim().length == 0) {
              this.selected = this.filteredItems;
              this.firstTime = false;
            }
          }
        } else {
          const response = await this.api_provider.post_bff_fastapi(url, postData, "post");
          if (find_value_current < this.find_value) return;
          if (response.data && response.data.data) {
            if (response.data.data.options_list) this.filteredItems = response.data.data.options_list;
            if (response.data.data.elements_selected && this.firstTime) {
              this.selected = response.data.data.elements_selected;
              this.firstTime = false;
              return;
            }
          }
        }
      } catch (error) {
        console.log("Error get_filters_options: ", error);
      }
    },
    handleChange() {
      let selectedValues = [];
      if (this.$props.typeFilter === "vehicle_plate") {
        selectedValues = this.selected.map(item => item.plate);
      } else {
        selectedValues = this.selected.map(item => item.id);
        if (this.$props.typeFilter === "highway"){
          this.$emit('update:highways', selectedValues);
        }
      }
      this.$emit('update:modelValue', selectedValues);
    },
    // Al llegar al tope solo quedan disponibles las opciones ya elegidas.
    isSelectable(option) {
      return !this.isFull || this.selected.some(item => item.id === option.id);
    },
    handleDeselect() { console.log("handleDeselect"); },
    closeMassivePaste() {
      this.massiveOpen = false;
      this.massiveText = "";
    },
    /**
     * Agrega a la selección las patentes pegadas que el usuario puede ver.
     *
     * Se normalizan con la misma función que la carga masiva de grupos de
     * vehículos y se validan contra get_vehicles_filter, que aplica el alcance del
     * usuario y deja solo vehículos habilitados. No se usa resolve-massive-plates
     * de grupos: exige permiso de crear grupos de vehículos e incluye deshabilitados.
     */
    async applyMassivePaste() {
      // VsudButton ejecuta el clic con retraso: un doble clic no consulta dos veces.
      if (this.massiveLoading) return;
      const plates = this.useful_functions.normalizeMassivePlatesInput(this.massiveText).split(",").filter(Boolean);
      if (plates.length === 0) {
        this.massiveResult = { message: "No se reconoció ninguna patente. El formato esperado es AAAA-11.", notFound: [] };
        return;
      }

      this.massiveLoading = true;
      try {
        const found = [];
        for (let start = 0; start < plates.length; start += VEHICLES_FILTER_LIMIT) {
          const response = await this.api_provider.post(this.urls_server.get_vehicles_filter, {
            search: "",
            plates_selected: plates.slice(start, start + VEHICLES_FILTER_LIMIT),
            ids_selected: [],
            id_company: this.securedWebStorage.getItem("id_company"),
            user_id: this.securedWebStorage.getItem("id"),
            system_role: this.securedWebStorage.getItem("role"),
          });
          found.push(...(response?.data?.options_list || []));
        }

        const key = (plate) => String(plate || "").replace(/-/g, "").toUpperCase();
        const current = new Set(this.selected.map((item) => key(item.plate)));
        const fresh = found.filter((item) => !current.has(key(item.plate)));
        const room = this.max ? Math.max(0, this.max - this.selected.length) : fresh.length;
        const added = fresh.slice(0, room);
        this.selected = [...this.selected, ...added];

        const foundKeys = new Set(found.map((item) => key(item.plate)));
        this.massiveResult = {
          message: added.length === 1 ? "Se agregó 1 patente." : `Se agregaron ${added.length} patentes.`,
          notFound: plates.filter((plate) => !foundKeys.has(key(plate))),
          overLimit: fresh.length - added.length,
        };
        this.closeMassivePaste();
      } catch (error) {
        console.log("Error applyMassivePaste: ", error);
        this.massiveResult = { message: "No se pudo validar el listado. Intenta de nuevo.", notFound: [] };
      } finally {
        this.massiveLoading = false;
      }
    },
    onLoad() {
      const loadTime = Date.now() - this.loadStart;
      if (loadTime >= this.minLoadTime) {
        this.showContent = true;
      } else {
        setTimeout(() => { this.showContent = true; }, this.minLoadTime - loadTime);
      }
    },
    async getAllHighways(url_get) {
      try {
        if (!this.allHighwaysList?.length) {
          const response = await this.api_provider.post(url_get, {}, "get");
          if (response.data && response.data.highways_list) this.allHighwaysList = response.data.highways_list;
        }
      } catch (error) { console.log(error); }
    },
    async getAllPorchs(url_get) {
      try {
        if (!this.allPorchsList?.length) {
          const response = await this.api_provider.post(url_get, {}, "get");
          if (response.data && response.data.porchs_list) this.allPorchsList = response.data.porchs_list;
        }
      } catch (error) { console.log(error); }
    },
    filterSelectSearchByInput(filterText, listToFilter) {
      if (!filterText) return listToFilter;
      this.filteredItems = listToFilter.filter(item => item.name.toLowerCase().includes(filterText.toLowerCase()));
      if (this.firstTime && this.searchQuery.trim().length == 0) {
        this.selected = this.filteredItems;
        this.firstTime = false;
      }
    },
    filterPorchsByHighway(highwayIds) {
      if (!this.allPorchsList?.length) return [];
      return this.allPorchsList.filter(item => highwayIds.includes(item.id_highway));
    }
  },
};
</script>

<style scoped>
.optional {
  color: var(--gray-text);
}

.massive-toggle {
  border: 0;
  background: none;
  color: var(--blue);
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.35rem 0;
}
.massive-toggle:hover {
  color: var(--blue-hover);
  text-decoration: underline;
}
.massive-panel {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.25rem;
}
.massive-panel textarea {
  border: 1px solid var(--light-gray);
  border-radius: 12px;
  font-size: 0.85rem;
}
.massive-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}
.massive-cancel {
  border: 0;
  background: none;
  color: var(--dark-gray);
  font-size: 0.82rem;
}
.massive-result {
  margin: 0.35rem 0 0;
  font-size: 0.8rem;
  color: var(--success);
}
.massive-result.has-missing {
  color: var(--orange);
}
.field-status {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}
.selection-counter {
  margin: 0;
  font-size: 0.8rem;
  color: var(--dark-gray);
  font-variant-numeric: tabular-nums;
}
.selection-counter.full {
  color: var(--orange);
  font-weight: 500;
}
.list-limit {
  margin: 0 0.5rem 0.35rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background-color: var(--light-orange);
  color: var(--orange);
  font-size: 0.8rem;
  list-style: none;
}
.option-plate {
  font-weight: 600;
}
.option-alias {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  color: var(--dark-gray);
}

.gray-border {
  border: var(--light-gray) solid 1px !important;
}

.valid-border {
  border: #66d432 solid 1px !important;
}

.invalid-border {
  border: #fd5c70 solid 1px !important;
}

.title {
  color: var(--black);
  font-size: 0.9rem;
  font-weight: 500;
}

select {
  border: 1px solid var(--light-gray);
  border-radius: 0.7rem;
}

select:focus {
  border: 1px solid var(--violet);
  outline: 0;
  box-shadow: none;
}

.was-validated .form-select:valid:focus,
.form-select.is-valid:focus,
.was-validated .form-select:invalid:focus,
.form-select.is-invalid:focus {
  box-shadow: none;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
}

.dropdown-menu {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem 0;
  border: 1px solid #ced4da;
  border-radius: 0.7rem;
  background-color: white;
}

.dropdown-menu li {
  cursor: pointer;
  padding: 0.5rem 1rem;
}

.dropdown-menu li:hover {
  background-color: #f2f2f2;
}

.v-select.form-control {
  border-radius: 0.75rem;
  padding: 0 6px 0 12px;
}

.disabled div.position-relative{
  pointer-events: none;
  color: #bfcbd9;
  cursor: not-allowed;
  background-image: none;
}

.disabled div.position-relative .v-select{
  background-color: #eef1f6;
  border-color: #d1dbe5;
}

</style>
<style>
.vs__search::placeholder {
  color: var(--gray-text);
}

.vs__dropdown-toggle {
  padding: 0.3rem 0;
  border: none;
}

.vs__selected {
  padding: 0 7px;
  margin: 2px;
}

.vs__actions .vs__open-indicator,
.vs__actions .vs__clear {
  background-repeat: no-repeat;
  background-position: center;
  background-size: 0.75rem;
}

.vs__actions .vs__open-indicator {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
}

.vs__actions .vs__clear {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3 3L13 13M13 3L3 13'/%3e%3c/svg%3e");
}

.vs__actions .vs__open-indicator path,
.vs__actions .vs__clear path {
  opacity: 0;
}

.vs__actions {
  padding-top: 0;
}

.vs__dropdown-menu {
  top: 44px;
  border-radius: 0.75rem;
  border: none;
  box-shadow: 0 8px 26px -4px rgba(20, 20, 20, 0.15), 0 8px 9px -5px rgba(20, 20, 20, 0.06);
  padding-top: 10px;
  padding-bottom: 10px;
}

.v-select.form-control .vs__selected-options {
  transition: max-height 0.3s ease;
  overflow-y: auto;
  max-height: calc(85.75px + 39.98px + 16px);
}

.v-select.form-control .vs__dropdown-menu {
  position: absolute;
  top: auto;
}

</style>